/* eslint-disable */
import { PRICING_COLUMN_CELL_VALUE_TYPES, COLUMN_TYPE_TO_VALUE_TYPE, PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';

/**
 * Helper function to get the value type for a column, respecting the format property if it exists
 *
 * @param {Object} column - The column object
 * @returns {string} The value type (e.g., 'amount', 'percent', 'number', 'text')
 *
 * @example
 * // Column with format property
 * getValueType({ type: 'tax', format: 'percent' }) // Returns 'percent'
 *
 * // Column without format property
 * getValueType({ type: 'tax' }) // Returns 'amount' (default for tax type)
 */
export const getValueType = (column) => {
  if (!column) return PRICING_COLUMN_CELL_VALUE_TYPES.TEXT;

  // If the column has a format property, use that to determine the value type
  if (column.format) {
    switch (column.format.toLowerCase()) {
      case 'percent':
        return PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT;
      case 'amount':
        return PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT;
      default:
        // Fall back to the default mapping if format is not recognized
        return COLUMN_TYPE_TO_VALUE_TYPE[column.type];
    }
  }

  // Otherwise use the default mapping
  return COLUMN_TYPE_TO_VALUE_TYPE[column.type];
};

/**
 * Parse the displayed value back to raw value for editing
 * Removes formatting characters like currency symbols and percentage signs
 *
 * @param {string} displayValue - The formatted display value
 * @param {string} type - The value type (from PRICING_COLUMN_CELL_VALUE_TYPES)
 * @returns {string} The raw value without formatting
 *
 * @example
 * parseDisplayValue('$123.45', 'amount') // Returns '123.45'
 * parseDisplayValue('15%', 'percent') // Returns '15'
 * parseDisplayValue('42', 'number') // Returns '42'
 */
export const parseDisplayValue = (displayValue, type) => {
  if (!displayValue) return '0';

  switch (type) {
    case PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT:
      // Remove currency symbol and parse
      return displayValue.toString().replace(/[^0-9.-]+/g, '');
    case PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT:
      // Remove percentage symbol
      return displayValue.toString().replace(/[%]/g, '');
    case PRICING_COLUMN_CELL_VALUE_TYPES.NUMBER:
      // Ensure it's a valid number
      const num = parseFloat(displayValue);
      return isNaN(num) ? '0' : num.toString();
    case PRICING_COLUMN_CELL_VALUE_TYPES.TEXT:
    default:
      return displayValue;
  }
};

/**
 * Format the value based on the cell type
 * Adds appropriate formatting like currency symbols and percentage signs
 *
 * @param {string|number} value - The raw value to format
 * @param {string} type - The value type (from PRICING_COLUMN_CELL_VALUE_TYPES)
 * @returns {string} The formatted value
 *
 * @example
 * formatCellValue('123.45', 'amount') // Returns '$123.45'
 * formatCellValue('15', 'percent') // Returns '15%'
 * formatCellValue('42', 'number') // Returns '42'
 * formatCellValue('', 'amount') // Returns '' (empty string)
 */
export const formatCellValue = (value, type) => {
  if (value === undefined || value === null || value === '') return '';

  switch (type) {
    case PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT:
      // Format as currency using Intl.NumberFormat
      const numValue = parseFloat(value);
      return isNaN(numValue)
        ? '$0.00'
        : new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numValue);
    case PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT:
      // Format as percentage with proper handling
      const percentValue = parseFloat(value);
      return isNaN(percentValue)
        ? '0%'
        : `${percentValue}%`;
    case PRICING_COLUMN_CELL_VALUE_TYPES.NUMBER:
      // Format as number with proper handling
      const numberValue = parseFloat(value);
      return isNaN(numberValue) ? '0' : numberValue.toString();
    case PRICING_COLUMN_CELL_VALUE_TYPES.TEXT:
    default:
      // Return as is for text
      return value;
  }
};

/**
 * Check if a value is already correctly formatted according to its type
 *
 * @param {string} value - The value to check
 * @param {string} type - The value type (from PRICING_COLUMN_CELL_VALUE_TYPES)
 * @returns {boolean} True if the value is correctly formatted, false otherwise
 *
 * @example
 * isFormattedCorrectly('$123.45', 'amount') // Returns true
 * isFormattedCorrectly('123.45', 'amount') // Returns false
 * isFormattedCorrectly('15%', 'percent') // Returns true
 */
export const isFormattedCorrectly = (value, type) => {
  if (!value) return true;

  switch (type) {
    case PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT:
      return value.toString().includes('$');
    case PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT:
      return value.toString().includes('%');
    default:
      return true;
  }
};

/**
 * Calculate the modified price for a single row, taking into account all modifiers
 *
 * @param {Array} columns - Array of column objects
 * @param {number} rowIndex - The index of the row to calculate
 * @returns {number} The calculated row price with all modifiers applied
 */
export const calculateModifiedRowPrice = (columns, rowIndex) => {
  if (!columns || columns.length === 0 || rowIndex < 0) return 0;

  // Don't filter out hidden columns - include all columns in calculation

  // Get the base price column
  const priceColumn = columns.find((col) => col.type === PRICING_COLUMN_TYPES.PRICE);
  if (!priceColumn || !priceColumn.cells[rowIndex]) return 0;

  const priceValueType = getValueType(priceColumn);
  const basePrice = parseFloat(parseDisplayValue(priceColumn.cells[rowIndex].value, priceValueType)) || 0;

  // Get the quantity column
  const quantityColumn = columns.find((col) => col.type === PRICING_COLUMN_TYPES.QUANTITY);
  let quantity = 0; // Default quantity is 1

  if (quantityColumn && quantityColumn.cells[rowIndex]) {
    const quantityValueType = getValueType(quantityColumn);
    quantity = parseFloat(parseDisplayValue(quantityColumn.cells[rowIndex].value, quantityValueType)) || 0;
  }

  // Start with the base price * quantity
  let runningTotal = basePrice * quantity;

  // Process modifiers in sequence, each building on the result of the previous one

  // 1. Apply discount modifiers first (subtract)
  const discountColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.DISCOUNT);
  discountColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal -= (basePrice * quantity * value / 100);
    } else {
      runningTotal -= value;
    }
  });

  // Ensure running total doesn't go below 0
  runningTotal = Math.max(0, runningTotal);

  // 2. Apply fee modifiers (add)
  const feeColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.FEE);
  feeColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100); // Apply to running total
    } else {
      runningTotal += value;
    }
  });

  // 3. Apply tax modifiers (add)
  const taxColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.TAX);
  taxColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100); // Apply to running total
    } else {
      runningTotal += value;
    }
  });

  // Ensure final price doesn't go below 0
  return Math.max(0, runningTotal);
};

/**
 * Calculate subtotal from columns
 * Sums up all rows with their modified prices (including quantity, fee, tax, discount)
 *
 * @param {Array} columns - Array of column objects
 * @returns {number} The calculated subtotal
 *
 * @example
 * // Columns with price, quantity, fee, tax, and discount
 * calculateSubtotal([
 *   { type: 'price', cells: [{ value: '$10.00' }, { value: '$20.00' }] },
 *   { type: 'quantity', cells: [{ value: '2' }, { value: '3' }] },
 *   { type: 'fee', cells: [{ value: '$1.00' }, { value: '5%' }] },
 *   { type: 'tax', cells: [{ value: '10%' }, { value: '$2.00' }] },
 *   { type: 'discount', cells: [{ value: '5%' }, { value: '$1.00' }] }
 * ]) // Returns the sum of all modified row prices
 */
export const calculateSubtotal = (columns) => {
  if (!columns || columns.length === 0) return 0;

  // Don't filter out hidden columns - include all columns in calculation

  // Find the price column to determine the number of rows
  const priceColumn = columns.find((col) => col.type === PRICING_COLUMN_TYPES.PRICE);
  if (!priceColumn) return 0;

  const rowCount = priceColumn.cells.length;
  let subtotal = 0;

  // For each row, calculate the modified price and add it to the subtotal
  for (let i = 0; i < rowCount; i++) {
    subtotal += calculateModifiedRowPrice(columns, i);
  }

  return subtotal;
};

/**
 * Apply a format change to a value
 * Converts the value to the new format while preserving the numeric value
 *
 * @param {string} value - The current value
 * @param {string} currentFormat - The current format
 * @param {string} newFormat - The new format to apply
 * @returns {string} The value in the new format
 *
 * @example
 * applyFormatChange('$100.00', 'amount', 'percent') // Returns '100%'
 * applyFormatChange('15%', 'percent', 'amount') // Returns '$15.00'
 */
export const applyFormatChange = (value, currentFormat, newFormat) => {
  // Parse the current value to a raw number
  const rawValue = parseDisplayValue(value, currentFormat);

  // Format according to the new format
  return formatCellValue(rawValue, newFormat);
};

/**
 * Get the appropriate default value for a column type and format
 *
 * @param {string} columnType - The column type
 * @param {string} format - The format (optional)
 * @returns {string} The default value
 *
 * @example
 * getDefaultValueForColumnType('tax', 'percent') // Returns '0%'
 * getDefaultValueForColumnType('discount') // Returns '0%'
 * getDefaultValueForColumnType('total') // Returns '$0.00'
 */
export const getDefaultValueForColumnType = (columnType, format) => {
  // Determine the value type based on column type and format
  const valueType = format
    ? (format === 'percent' ? PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT : PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT)
    : COLUMN_TYPE_TO_VALUE_TYPE[columnType];

  // Return formatted default value
  return formatCellValue('0', valueType);
};

/**
 * Calculate the total from the subtotal and all modifiers in the summary columns
 *
 * @param {number} subtotal - The calculated subtotal
 * @param {Array} summaryColumns - Array of summary column objects
 * @returns {number} The calculated total
 */
export const calculateTotal = (subtotal, summaryColumns) => {
  if (!summaryColumns || !summaryColumns.length) return subtotal;

  let runningTotal = subtotal;

  // Process summary modifiers in sequence

  // 1. Apply discount modifiers first (subtract)
  const discountColumns = summaryColumns.filter((col) => col.type === PRICING_COLUMN_TYPES.DISCOUNT);

  discountColumns.forEach((column) => {
    if (!column.value) return;

    const valueType = getValueType(column);
    const rawValue = parseDisplayValue(column.value, valueType);
    const value = parseFloat(rawValue);

    if (isNaN(value) || value === 0) return;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal -= (subtotal * value / 100);
    } else {
      runningTotal -= value;
    }
  });

  // Ensure running total doesn't go below zero
  runningTotal = Math.max(0, runningTotal);

  // 2. Apply fee modifiers (add)
  const feeColumns = summaryColumns.filter((col) => col.type === PRICING_COLUMN_TYPES.FEE);

  feeColumns.forEach((column) => {
    if (!column.value) return;

    const valueType = getValueType(column);
    const rawValue = parseDisplayValue(column.value, valueType);
    const value = parseFloat(rawValue);

    if (isNaN(value) || value === 0) return;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100);
    } else {
      runningTotal += value;
    }
  });

  // 3. Apply tax modifiers (add)
  const taxColumns = summaryColumns.filter((col) => col.type === PRICING_COLUMN_TYPES.TAX);

  taxColumns.forEach((column) => {
    if (!column.value) return;

    const valueType = getValueType(column);
    const rawValue = parseDisplayValue(column.value, valueType);
    const value = parseFloat(rawValue);

    if (isNaN(value) || value === 0) return;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100);
    } else {
      runningTotal += value;
    }
  });

  // Ensure total doesn't go below zero
  return Math.max(0, runningTotal);
};

/**
 * Get the display value for an input based on focus state
 * Returns raw value when focused, formatted value when not focused
 *
 * @param {boolean} isFocused - Whether the input is focused
 * @param {string} inputValue - The current input value (raw)
 * @param {string} formattedValue - The formatted display value
 * @returns {string} The appropriate display value
 *
 * @example
 * getInputDisplayValue(true, '123.45', '$123.45') // Returns '123.45'
 * getInputDisplayValue(false, '123.45', '$123.45') // Returns '$123.45'
 */
export const getInputDisplayValue = (isFocused, inputValue, formattedValue) => {
  return isFocused ? (inputValue || '') : (formattedValue || '');
};

/**
 * Filter columns by visibility
 * Returns visible or hidden columns based on the parameter
 *
 * @param {Array} columns - Array of column objects
 * @param {boolean} visible - Whether to return visible (true) or hidden (false) columns
 * @returns {Array} Filtered columns
 *
 * @example
 * // Get visible columns
 * filterColumnsByVisibility([
 *   { id: 1, hidden: false },
 *   { id: 2, hidden: true }
 * ], true) // Returns [{ id: 1, hidden: false }]
 *
 * // Get hidden columns
 * filterColumnsByVisibility([
 *   { id: 1, hidden: false },
 *   { id: 2, hidden: true }
 * ], false) // Returns [{ id: 2, hidden: true }]
 */
export const filterColumnsByVisibility = (columns, visible = true) => {
  if (!columns) return [];
  return columns.filter((column) => (visible ? !column.hidden : column.hidden));
};

/**
 * Group summary columns by type
 * Organizes summary columns into categories (tax, fee, discount, total, subtotal)
 *
 * @param {Array} summaryColumns - Array of summary column objects
 * @returns {Object} Object with arrays for each type of summary column
 *
 * @example
 * groupSummaryColumnsByType([
 *   { id: 1, type: 'tax', value: '10%' },
 *   { id: 2, type: 'discount', value: '$5.00' }
 * ]) // Returns { tax: [{ id: 1, type: 'tax', value: '10%' }], fee: [], discount: [{ id: 2, type: 'discount', value: '$5.00' }], total: [], subtotal: [] }
 */
export const groupSummaryColumnsByType = (summaryColumns) => {
  if (!summaryColumns) return { tax: [], fee: [], discount: [], total: [], subtotal: [] };

  const result = {
    tax: [],
    fee: [],
    discount: [],
    total: [],
    subtotal: []
  };

  summaryColumns.forEach((column) => {
    switch (column.type) {
      case PRICING_COLUMN_TYPES.TAX:
        result.tax.push(column);
        break;
      case PRICING_COLUMN_TYPES.FEE:
        result.fee.push(column);
        break;
      case PRICING_COLUMN_TYPES.DISCOUNT:
        result.discount.push(column);
        break;
      case PRICING_COLUMN_TYPES.TOTAL:
        result.total.push(column);
        break;
      case PRICING_COLUMN_TYPES.SUBTOTAL:
        result.subtotal.push(column);
        break;
      default:
        break;
    }
  });

  return result;
};

/**
 * Calculate the total for a specific row based on price and quantity
 *
 * @param {Array} columns - Array of column objects
 * @param {number} rowIndex - The index of the row to calculate
 * @returns {number} The calculated row total
 *
 * @example
 * // Calculate row total for the first row
 * calculateRowTotal([
 *   { type: 'price', cells: [{ value: '$10.00' }, { value: '$20.00' }] },
 *   { type: 'quantity', cells: [{ value: '2' }, { value: '3' }] }
 * ], 0) // Returns 20 (10*2)
 */
export const calculateRowTotal = (columns, rowIndex) => {
  if (!columns || columns.length === 0 || rowIndex < 0) return 0;

  // Identify price and quantity columns - include hidden columns
  const priceColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.AMOUNT ||
    col.type === 'price' ||
    col.type === 'subtotal');

  const quantityColumns = columns.filter((col) => col.type === 'quantity' ||
    col.type === PRICING_COLUMN_TYPES.QUANTITY);

  // If we have both price and quantity columns
  if (priceColumns.length > 0 && quantityColumns.length > 0) {
    // Get price for this row (use the first price column)
    const priceColumn = priceColumns[0];
    const priceCell = priceColumn.cells[rowIndex];
    if (!priceCell) return 0;

    const priceValueType = getValueType(priceColumn);
    const price = parseFloat(parseDisplayValue(priceCell.value, priceValueType)) || 0;

    // Get quantity for this row (use the first quantity column)
    const quantityColumn = quantityColumns[0];
    const quantityCell = quantityColumn.cells[rowIndex];
    if (!quantityCell) return 0;

    const quantityValueType = getValueType(quantityColumn);
    const quantity = parseFloat(parseDisplayValue(quantityCell.value, quantityValueType)) || 0;

    // Return price * quantity
    return price * quantity;
  }
  // If we only have price columns
  else if (priceColumns.length > 0) {
    // Get price for this row (use the first price column)
    const priceColumn = priceColumns[0];
    const priceCell = priceColumn.cells[rowIndex];
    if (!priceCell) return 0;

    const priceValueType = getValueType(priceColumn);
    const price = parseFloat(parseDisplayValue(priceCell.value, priceValueType)) || 0;

    return price;
  }

  return 0;
};

/**
 * Apply a modifier to a column cell value depending on the column type
 *
 * @param {number} baseValue - The base value to modify
 * @param {Object} column - The modifier column
 * @param {number} rowIndex - The row index
 * @param {number} quantity - The quantity
 * @param {boolean} isDiscount - Whether this is a discount (subtract) or addition
 * @returns {number} The modifier amount
 */
export const applyModifier = (baseValue, column, rowIndex, quantity, isDiscount = false) => {
  if (!column.cells[rowIndex]) return 0;

  const valueType = getValueType(column);
  const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

  let modifierAmount = 0;
  if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
    modifierAmount = (baseValue * quantity * value / 100);
  } else {
    modifierAmount = value;
  }

  return isDiscount ? -modifierAmount : modifierAmount;
};

/**
 * Process all modifiers of a specific column type
 *
 * @param {Array} columns - Filtered visible columns
 * @param {string} columnType - The type of column to process
 * @param {number} basePrice - The base price
 * @param {number} rowIndex - The row index
 * @param {number} quantity - The quantity
 * @param {boolean} isDiscount - Whether this is a discount type
 * @returns {number} The total modifier amount
 */
export const processModifiers = (columns, columnType, basePrice, rowIndex, quantity, isDiscount = false) => {
  const modifierColumns = columns.filter((col) => col.type === columnType);

  return modifierColumns.reduce((total, column) => {
    return total + applyModifier(basePrice, column, rowIndex, quantity, isDiscount);
  }, 0);
};

/**
 * Calculate subtotal for a specific row
 * Calculates the subtotal for a specific row based on price, quantity, and modifiers
 *
 * @param {Array} columns - Array of column objects
 * @param {number} rowIndex - The index of the row to calculate
 * @returns {number} The calculated subtotal for the row
 */
export const calculateRowSubtotal = (columns, rowIndex) => {
  if (!columns || columns.length === 0 || rowIndex < 0) return 0;

  // Get the base price column
  const priceColumn = columns.find((col) => col.type === PRICING_COLUMN_TYPES.PRICE);
  if (!priceColumn || !priceColumn.cells[rowIndex]) return 0;

  const priceValueType = getValueType(priceColumn);
  const basePrice = parseFloat(parseDisplayValue(priceColumn.cells[rowIndex].value, priceValueType)) || 0;

  // Get the quantity column
  const quantityColumn = columns.find((col) => col.type === PRICING_COLUMN_TYPES.QUANTITY);
  let quantity = 0;

  if (quantityColumn && quantityColumn.cells[rowIndex]) {
    const quantityValueType = getValueType(quantityColumn);
    quantity = parseFloat(parseDisplayValue(quantityColumn.cells[rowIndex].value, quantityValueType)) || 0;
  }

  // Start with the base price * quantity
  let runningTotal = basePrice * quantity;

  // Process modifiers in sequence, each building on the result of the previous one
  // 1. Apply discount modifiers first (subtract)
  const discountColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.DISCOUNT);
  discountColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal -= (basePrice * quantity * value / 100);
    } else {
      runningTotal -= value;
    }
  });

  // Ensure running total doesn't go below 0
  runningTotal = Math.max(0, runningTotal);

  // 2. Apply fee modifiers (add)
  const feeColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.FEE);
  feeColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100); // Apply to running total
    } else {
      runningTotal += value;
    }
  });

  // 3. Apply tax modifiers (add)
  const taxColumns = columns.filter((col) => col.type === PRICING_COLUMN_TYPES.TAX);
  taxColumns.forEach((column) => {
    if (!column.cells[rowIndex]) return;

    const valueType = getValueType(column);
    const value = parseFloat(parseDisplayValue(column.cells[rowIndex].value, valueType)) || 0;

    if (valueType === PRICING_COLUMN_CELL_VALUE_TYPES.PERCENT) {
      runningTotal += (runningTotal * value / 100); // Apply to running total
    } else {
      runningTotal += value;
    }
  });

  // Ensure final subtotal doesn't go below 0
  return Math.max(0, runningTotal);
};

/**
 * Get initial input values for all editable summary columns
 *
 * @param {Array} summaryColumns - Array of summary column objects
 * @returns {Object} Mapping of column IDs to their parsed display values
 */
export const getInitialInputValues = (summaryColumns) => {
  if (!summaryColumns || !summaryColumns.length) return {};

  const initialInputValues = {};

  summaryColumns.forEach((column) => {
    // Skip SUBTOTAL and TOTAL columns as they're calculated
    if (column.type !== PRICING_COLUMN_TYPES.SUBTOTAL && column.type !== PRICING_COLUMN_TYPES.TOTAL) {
      const valueType = getValueType(column);
      // Ensure we have a valid display value to parse
      const displayValue = column.value || formatCellValue(0, valueType);
      initialInputValues[column._id] = parseDisplayValue(displayValue, valueType);
    }
  });

  return initialInputValues;
};

/**
 * Format all values in summary columns that are not already correctly formatted
 *
 * @param {Array} summaryColumns - Array of summary column objects
 * @returns {Array} Array of objects containing columnIndex and formattedValue for columns that need updating
 */
export const formatSummaryColumnValues = (summaryColumns) => {
  if (!summaryColumns || !summaryColumns.length) return [];

  const updates = [];

  summaryColumns.forEach((column, index) => {
    // Skip SUBTOTAL and TOTAL columns as they're calculated
    if (column.type !== PRICING_COLUMN_TYPES.SUBTOTAL && column.type !== PRICING_COLUMN_TYPES.TOTAL) {
      const valueType = getValueType(column);

      // Format the value if it doesn't exist or isn't properly formatted
      if (!column.value || !isFormattedCorrectly(column.value, valueType)) {
        const rawValue = column.value ? parseDisplayValue(column.value, valueType) : '0';
        const formattedValue = formatCellValue(rawValue, valueType);

        updates.push({ columnIndex: index, formattedValue });
      }
    }
  });

  return updates;
};

/**
 * Format values when unfocused and incorrectly formatted
 *
 * @param {Array} summaryColumns - Array of summary column objects
 * @param {Object} focusedInputs - Object mapping column IDs to focus state (true/false)
 * @returns {Array} Array of objects containing columnIndex and formattedValue for columns that need updating
 */
export const formatUnfocusedSummaryValues = (summaryColumns, focusedInputs) => {
  if (!summaryColumns || !summaryColumns.length) return [];

  const updates = [];

  summaryColumns.forEach((column) => {
    // Skip SUBTOTAL and TOTAL columns as they're calculated
    if (column.type !== PRICING_COLUMN_TYPES.SUBTOTAL && column.type !== PRICING_COLUMN_TYPES.TOTAL) {
      // Only format if the input is not focused and the value is not already formatted correctly
      if (!focusedInputs[column._id] && column.value && !isFormattedCorrectly(column.value, getValueType(column))) {
        const valueType = getValueType(column);
        const rawValue = parseDisplayValue(column.value, valueType);
        const formattedValue = formatCellValue(rawValue, valueType);

        const columnIndex = summaryColumns.findIndex((col) => col._id === column._id);
        if (columnIndex !== -1) {
          updates.push({ columnIndex, formattedValue });
        }
      }
    }
  });

  return updates;
};

/**
 * Calculate summary data from columns and summary columns
 *
 * @param {Array} columns - Array of column objects
 * @param {Array} summaryColumns - Array of summary column objects
 * @returns {Object} Calculated summary data
 */
export const calculateSummaryData = (columns, summaryColumns) => {
  // Initialize summary data with an empty object
  const data = {};

  // Calculate subtotal from columns - this will consider all relevant columns
  const subtotal = calculateSubtotal(columns);

  // Process each summary column individually
  if (summaryColumns && summaryColumns.length > 0) {
    summaryColumns.forEach((column) => {
      if (column.type === PRICING_COLUMN_TYPES.SUBTOTAL) {
        // For subtotal columns, use the calculated subtotal
        data[column._id] = formatCellValue(subtotal, PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT);
      }
      else if (column.type === PRICING_COLUMN_TYPES.TOTAL) {
        // For total columns, calculate the full total with all modifiers
        const calculatedTotal = calculateTotal(subtotal, summaryColumns);
        data[column._id] = formatCellValue(calculatedTotal, PRICING_COLUMN_CELL_VALUE_TYPES.AMOUNT);
      }
      // For other column types (tax, fee, discount), use their existing values
      // These values will be edited by the user
    });
  }

  // Add the raw calculated values for reference
  data.rawSubtotal = subtotal;
  data.rawTotal = calculateTotal(subtotal, summaryColumns);

  return data;
};
